import React from 'react';
import { InputLabel, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { withCurrentUser, withCurrentUserPermissionsByGroup } from '../../../state/auth';
import { PermissionsGroupId } from '../../../API';
import { useTheme } from '../../../hooks';

export const testIds = {
  root: 'permissions-group-selector.root',
  option: 'permissions-group-selector.option'
};

type PermissionsGroupsSelectorProps = {
  values?: PermissionsGroupId[];
  required?: boolean;
  onChange: (groupIds: PermissionsGroupId[] | undefined) => void;
  error?: boolean;
} & Record<string, unknown>;

export function PermissionsGroupsSelector({
  values,
  onChange,
  required,
  error,
  ...rest
}: PermissionsGroupsSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const { formControlColor } = useTheme();
  const userPermissionsByGroup = useRecoilValue(withCurrentUserPermissionsByGroup);
  const currentUser = useRecoilValue(withCurrentUser);
  const permissionGroupIds = Object.values(PermissionsGroupId);

  const currentUserPermissionsGroups = currentUser?.permissionsGroups?.map((group) => group.group);

  let permissionGroupIdsFiltered = permissionGroupIds;

  if (!currentUserPermissionsGroups?.includes(PermissionsGroupId.ADMIN)) {
    permissionGroupIdsFiltered = permissionGroupIds
      .filter((group) => userPermissionsByGroup[group]?.UPSERT)
      .filter((group) => currentUserPermissionsGroups?.includes(group));
  }

  const handleOnChange = (values: PermissionsGroupId[]) => {
    onChange(values);
  };

  const selectedValues = values?.filter((value) => permissionGroupIds.includes(value as PermissionsGroupId)) ?? [];

  return (
    <>
      <InputLabel required error={error}>
        {t('permissions.permissions_groups')}
      </InputLabel>
      <Select
        value={selectedValues}
        onChange={({ target: { value } }) => handleOnChange(value as PermissionsGroupId[])}
        data-testid={testIds.root}
        error={error}
        color={formControlColor}
        multiple
        required={required}
        {...rest}
      >
        {permissionGroupIds.map((option) => (
          <MenuItem
            key={option}
            value={option}
            disabled={!permissionGroupIdsFiltered.includes(option)}
            selected={selectedValues?.includes(option)}
            data-testid={testIds.option}
          >
            {option}
          </MenuItem>
        ))}
      </Select>
    </>
  );
}
